import React, { useCallback, useEffect, useRef, useState } from 'react';
import { HelpCircle } from 'lucide-react';
import { createPortal } from 'react-dom';

interface InfoTooltipProps {
  text: string;
  className?: string;
}

type TooltipPlacement = 'top' | 'bottom';

const TOOLTIP_WIDTH = 224;
const VIEWPORT_PADDING = 8;
const OFFSET = 10;

export const InfoTooltip: React.FC<InfoTooltipProps> = ({ text, className = '' }) => {
  const triggerRef = useRef<HTMLSpanElement | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState<{ left: number; top: number; placement: TooltipPlacement }>({
    left: 0,
    top: 0,
    placement: 'top',
  });

  const updatePosition = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;

    const rect = trigger.getBoundingClientRect();
    const halfWidth = TOOLTIP_WIDTH / 2;
    const minLeft = VIEWPORT_PADDING + halfWidth;
    const maxLeft = window.innerWidth - VIEWPORT_PADDING - halfWidth;
    const centeredLeft = rect.left + rect.width / 2;
    const left = Math.min(Math.max(centeredLeft, minLeft), maxLeft);

    const hasSpaceAbove = rect.top >= 80;
    const placement: TooltipPlacement = hasSpaceAbove ? 'top' : 'bottom';
    const top = placement === 'top' ? rect.top - OFFSET : rect.bottom + OFFSET;

    setPosition({ left, top, placement });
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    const onScrollOrResize = () => updatePosition();
    window.addEventListener('resize', onScrollOrResize);
    window.addEventListener('scroll', onScrollOrResize, true);

    return () => {
      window.removeEventListener('resize', onScrollOrResize);
      window.removeEventListener('scroll', onScrollOrResize, true);
    };
  }, [isOpen, updatePosition]);

  return (
    <span
      ref={triggerRef}
      className={`inline-flex items-center leading-none align-middle ${className}`}
      onMouseEnter={() => {
        updatePosition();
        setIsOpen(true);
      }}
      onMouseLeave={() => setIsOpen(false)}
      onFocus={() => {
        updatePosition();
        setIsOpen(true);
      }}
      onBlur={() => setIsOpen(false)}
      tabIndex={0}
    >
      <HelpCircle
        size={14}
        className="block text-gray-400 hover:text-indigo-500 transition-colors cursor-help"
      />
      {isOpen && createPortal(
        <span
          className="pointer-events-none fixed z-[300] bg-gray-900 text-white text-xs rounded-lg px-4 py-3 w-56 shadow-lg whitespace-normal text-center leading-snug"
          style={{
            left: position.left,
            top: position.top,
            transform: position.placement === 'top' ? 'translate(-50%, -100%)' : 'translate(-50%, 0)',
          }}
          role="tooltip"
        >
          {text}
          {position.placement === 'top' ? (
            <span className="absolute left-1/2 -translate-x-1/2 top-full w-0 h-0 border-x-4 border-x-transparent border-t-4 border-t-gray-900" />
          ) : (
            <span className="absolute left-1/2 -translate-x-1/2 -top-1 w-0 h-0 border-x-4 border-x-transparent border-b-4 border-b-gray-900" />
          )}
        </span>,
        document.body
      )}
    </span>
  );
};
